import type { BuildingKind, PermanentBuildingKind } from '../simulation/entities/Building.js';

export interface BuildingDef {
  kind: BuildingKind;
  cost: number;
  buildTimeSec: number;
  maxHp: number;
  vision: number;
  // Production Hubs queue squads; turrets ignore this and shoot.
  productionSlots: number;
  // Turret-only combat tuning. Hubs ignore these.
  dps: number;
  range: number;
  damagePerShot: number;
  attackCooldownSec: number;
  projectileSpeed: number;
}

// Tuning numbers from docs/prompts/04_production_buildings.md (hub, turret) and
// docs/02_GAME_DESIGN.md §2 + abilities table (mini_turret). Frozen — runtime
// systems read but never mutate. The Production Hub's productionSlots=2 caps the
// queue length; turret combat fields drive CombatSystem when operational.
// Invariant for turrets: damagePerShot / attackCooldownSec ≈ dps. Tweak together.
//
// mini_turret is spawned by AbilitySystem (Engineer's Deploy Mini-Turret), not via
// BuildCommand; cost/buildTime are kept at 0 because it skips the BuildCommand path.
// V2 "Tempo" patch (2026-05-24): hub buildTime 20→15 s, turret buildTime 12→9 s.
// Pairs with the SQUAD_DEFS buildTime cuts in data/squads.ts. Costs / HP / DPS
// unchanged so the strategic value of each building is identical — only the
// time-to-impact shrinks.
export const BUILDINGS: Record<BuildingKind, BuildingDef> = {
  production_hub: {
    kind: 'production_hub',
    cost: 200,
    buildTimeSec: 15,
    maxHp: 600,
    vision: 4,
    productionSlots: 2,
    dps: 0,
    range: 0,
    damagePerShot: 0,
    attackCooldownSec: 0,
    projectileSpeed: 0,
  },
  turret: {
    kind: 'turret',
    cost: 120,
    buildTimeSec: 9,
    maxHp: 400,
    vision: 7,
    productionSlots: 0,
    dps: 30,
    range: 400,
    damagePerShot: 15,
    attackCooldownSec: 0.5,
    projectileSpeed: 760,
  },
  mini_turret: {
    kind: 'mini_turret',
    cost: 0,
    buildTimeSec: 0,
    maxHp: 120,
    vision: 6,
    productionSlots: 0,
    dps: 16,
    range: 320,
    damagePerShot: 8,
    attackCooldownSec: 0.5,
    projectileSpeed: 700,
  },
};

// Player-buildable kinds — used by the BuildPanel and BuildCommand validation.
// mini_turret is excluded; it spawns from Engineer's Q ability, never the panel.
export const PERMANENT_BUILDING_KINDS: PermanentBuildingKind[] = ['production_hub', 'turret'];

export const MAX_HUBS_PER_PLAYER = 2;
export const BUILD_SLOTS_PER_CORE = 6;
